'use client';

import React, { useMemo, useState } from 'react';
import currencyPipe from 'src/utils/currencyPipe';
import InlineLoadingBar from './InlineLoadingBar/InlineLoadingBar';
import {
	Line,
	LineChart,
	CartesianGrid,
	DotProps,
	ResponsiveContainer,
	Tooltip as ChartsTooltip,
	TooltipProps,
	XAxis,
	YAxis,
	ReferenceLine,
} from 'recharts';
import {
	getAreaStops,
	getNegativeRedColor,
	getPositiveGreenColor,
	getTimeBasedTickFormatter,
	getTimeBasedXAxisTicks,
	getTooltipDate,
	TICK_DURATION_MAP,
} from 'src/utils/graph';
import ActiveDot from './Graph/ActiveDot';
import { SimpleFundingRateRecord } from 'src/@types/types';
import { signDependentValue } from 'src/utils/signDependentValue';
import { getColorVar } from 'src/utils/theme/colorVars';

type FundingChartProps = {
	page: string;
	marketIndex: number;
	fundingRateData: SimpleFundingRateRecord[];
	fundingInterval: '1h' | '8h' | 'APR';
	fundingPeriod: '1D' | '7D' | '1M';
};

const FUNDING_RATE_LINE_COLOR_ID = 'funding-rate-line-color';

const CustomActiveDot = (
	props: DotProps & { payload: { fundingRate: number } }
) => {
	const { cx = 0, cy = 0, payload } = props;

	const stroke = signDependentValue(payload.fundingRate, {
		positive: getPositiveGreenColor(),
		negative: getNegativeRedColor(),
		zero: getColorVar('text-default'),
	});

	return <ActiveDot x={cx} y={cy} stroke={stroke} />;
};

const CustomTooltip = ({
	active,
	payload,
	isHourly,
}: TooltipProps<any, any> & { isHourly: boolean }) => {
	if (active && payload && payload.length) {
		const data = payload[0]?.payload;

		if (!data) {
			return null;
		}

		const dateText = getTooltipDate(data.ts, { isRelativeToToday: isHourly });
		const fundingRateText = `${data.fundingRate.toFixed(4)}%`;
		const marketPriceText = currencyPipe(Number(data.marketPrice), '$', 2);

		const fundingRateTextColor = signDependentValue(data.fundingRate, {
			positive: 'text-text-positive-green-button',
			negative: 'text-text-negative-red-button',
			zero: 'text-text-default',
		});

		return (
			<div className="flex flex-col rounded-sm bg-tooltip-bg p-2 typo-b4 shadow-sm">
				<span className="mb-2 text-text-secondary">{dateText}</span>
				<span>
					<span className="text-text-default">Funding: </span>
					<span className={fundingRateTextColor}>{fundingRateText}</span>
				</span>
				<span className="text-text-default">
					Market Price: {marketPriceText}
				</span>
			</div>
		);
	}
};

const FundingPageChart = (props: FundingChartProps) => {
	const [loading, _setLoading] = useState(false);

	const displayedData = useMemo(() => {
		const combinedData: Pick<
			SimpleFundingRateRecord,
			'ts' | 'marketPrice' | 'fundingRate'
		>[] = [];
		props.fundingRateData.forEach((fundingRateDataPoint, index) => {
			if (index !== 0) {
				// if the previous funding rate is positive and the current is negative or vice versa
				// insert a zero funding rate point between them
				if (
					[
						fundingRateDataPoint.fundingRate,
						props.fundingRateData[index - 1].fundingRate,
					].filter((value) => value >= 0).length === 1
				) {
					combinedData.push({
						ts:
							(props.fundingRateData[index - 1].ts + fundingRateDataPoint.ts) /
							2,
						fundingRate: 0,
						marketPrice:
							(props.fundingRateData[index - 1]?.marketPrice +
								fundingRateDataPoint.marketPrice) /
							2,
					});
				}
			}

			combinedData.push({
				ts: fundingRateDataPoint.ts,
				fundingRate: fundingRateDataPoint.fundingRate,
				marketPrice: fundingRateDataPoint.marketPrice,
			});
		});

		return combinedData;
	}, [props.fundingInterval, props.fundingPeriod, props.fundingRateData]);

	const minFundingRate = Math.min(
		...displayedData.map((point) => point.fundingRate)
	);
	const maxFundingRate = Math.max(
		...displayedData.map((point) => point.fundingRate)
	);

	const lastDateMs = displayedData[0]?.ts ?? 0;
	const firstDateMs = displayedData[displayedData.length - 1]?.ts ?? 0;

	return (
		<div className="flex h-full flex-col space-y-4 overflow-hidden p-1 sm:p-6 sm:px-0">
			<div className="w-full grow overflow-hidden">
				{!isNaN(props.marketIndex) && props.page == 'funding' && !loading ? (
					<ResponsiveContainer
						id="market-funding-history"
						className={'min-h-[400px] min-w-[400px]'}
					>
						<LineChart data={displayedData}>
							<CartesianGrid stroke={getColorVar('stroke-divider')} />

							{/** funding rate y-axis */}
							<YAxis
								yAxisId="left"
								tickMargin={8}
								domain={['auto', 'auto']}
								stroke={getColorVar('stroke-divider')}
								tickFormatter={(tick) => `${tick}%`}
								// text css are found in index.css
							/>
							{/** 0 y-axis reference line */}
							<ReferenceLine
								y={0}
								yAxisId="left"
								strokeWidth={2}
								stroke={getColorVar('text-disabled')}
							/>

							{/** market price y-axis */}
							<YAxis
								yAxisId="right"
								tickMargin={8}
								domain={['auto', 'auto']}
								orientation="right"
								stroke={getColorVar('stroke-divider')}
								// text css are found in index.css
							/>
							<XAxis
								type="number"
								dataKey={'ts'}
								domain={['dataMin', 'dataMax']}
								stroke={getColorVar('stroke-divider')}
								ticks={getTimeBasedXAxisTicks(
									firstDateMs,
									lastDateMs,
									TICK_DURATION_MAP[
										props.fundingPeriod as keyof typeof TICK_DURATION_MAP
									]
								)}
								tickFormatter={getTimeBasedTickFormatter()}
								tickMargin={8}
								// text css are found in index.css
							/>

							{/** market price line graph */}
							<Line
								yAxisId="right"
								dataKey="marketPrice"
								dot={false}
								stroke={getColorVar('text-disabled')}
								strokeWidth={2}
							/>
							{/** funding rate colored line graph */}
							<defs>
								<linearGradient
									id={FUNDING_RATE_LINE_COLOR_ID}
									x1="0"
									y1="0"
									x2="0"
									y2="1"
								>
									{getAreaStops(minFundingRate, maxFundingRate)}
								</linearGradient>
							</defs>
							<Line
								yAxisId="left"
								dataKey="fundingRate"
								dot={false}
								strokeWidth={2}
								stroke={`url(#${FUNDING_RATE_LINE_COLOR_ID})`}
								activeDot={(props: DotProps) => (
									<CustomActiveDot
										{...(props as DotProps & {
											payload: { fundingRate: number };
										})}
									/>
								)}
								type={'monotone'}
							/>

							<ChartsTooltip
								content={(tooltipProps) => (
									<CustomTooltip
										{...tooltipProps}
										isHourly={props.fundingPeriod === '1D'}
									/>
								)}
								cursor={{
									stroke: getColorVar('text-secondary'),
									strokeDasharray: '4',
								}}
							/>
						</LineChart>
					</ResponsiveContainer>
				) : loading ? (
					<div className="flex h-full w-full items-center justify-center">
						<InlineLoadingBar />
					</div>
				) : (
					<div className="flex h-full w-full items-center justify-center text-text-default">
						Please select a market to view funding
					</div>
				)}
			</div>
		</div>
	);
};

export default React.memo(FundingPageChart);
